import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { X, Funnel, CaretDown, CalendarBlank } from '@phosphor-icons/react';
import { Calendar } from '@/components/ui/calendar';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

// ── Filter shape ─────────────────────────────────────────────────────────────

export interface ActivityFilters {
  actorUserIds: string[];
  actions: string[];
  source: '' | 'human' | 'ai';
  createdAfter: string;  // datetime-local: YYYY-MM-DDTHH:mm
  createdBefore: string; // datetime-local: YYYY-MM-DDTHH:mm
}

export const EMPTY_ACTIVITY_FILTERS: ActivityFilters = {
  actorUserIds: [],
  actions: [],
  source: '',
  createdAfter: '',
  createdBefore: '',
};

// NOTICE_ACTION_GROUP is the set of audit actions the notices surface produces
// (mirrors the internal/audit notice_*/fee_*/notice_run_* constants). Powers
// the one-click "Notices" preset and the ?preset=notices deep-link from the
// Notices → Members tab, so admins land on the fee/notice history pre-filtered.
export const NOTICE_ACTION_GROUP: string[] = [
  'notice_config_upsert',
  'notice_category_config_upsert',
  'notice_send',
  'notice_paid',
  'notice_deregister',
  'notice_reactivate',
  'notice_remind',
  'notice_overdue',
  'notice_consequence',
  'notice_cancel',
  'notice_tryout_start',
  'notice_approve',
  'notice_decline',
  'notice_seat_book',
  'notice_seat_waitlist',
  'notice_seat_release',
  'notice_seat_auto_offer',
  'notice_subscription_start',
  'notice_subscription_cancel',
  'notice_subscription_pause',
  'notice_subscription_resume',
  'notice_subscription_charge',
  'fee_profile_create',
  'fee_profile_update',
  'fee_profile_soft_delete',
  'fee_profile_restore',
  'fee_rule_create',
  'fee_rule_update',
  'fee_rule_soft_delete',
  'fee_rule_restore',
  'notice_run_start',
  'notice_run_complete',
  'notice_run_failed',
  'notice_run_cancelled',
];

// EXPLICIT_ACTION_LABELS lists the actions whose summary template collapses to
// something useless once the placeholders are stripped — "Updated
// {{first_name}} {{last_name}}" would read as just "Updated". Those get a
// hand-written filter label; every other action derives one from its summary.
const EXPLICIT_ACTION_LABELS = new Set([
  'match_event_append',
  'media_asset_delete',
  'media_asset_upload',
  'member_update',
  'refund_succeeded',
]);

// actionFilterLabel turns an audit action into a name for the filter list.
//
// The list needs a label for the action TYPE, but the only string we have per
// action is its feed summary — a template written around the row's metadata
// ("Created activity “{{title}}”"). Rendering it with no interpolation left
// the punctuation the placeholder sat inside behind, so the picker showed
// `Created activity “ ”` and `Confirmed attendance ( change(s))`.
//
// So strip each placeholder together with whatever wrapped it: the quotes
// around it, a parenthetical that exists only to hold it, a possessive 's, the
// · separators between metadata segments. What's left is the invariant part of
// the sentence, which is exactly the action type.
export function actionFilterLabel(t: TFunction<'common'>, action: string): string {
  if (EXPLICIT_ACTION_LABELS.has(action)) {
    return t(`settings.activity.filter.action.label.${action}`, { defaultValue: action });
  }
  const summary = t(`settings.activity.action.${action}.summary`, { defaultValue: '' });
  if (!summary) return action;
  const cleaned = summary
    // A parenthetical built around a placeholder goes whole — including one
    // level of nesting, as in "({{cells_changed}} change(s))".
    .replace(/\s*\((?:[^(){}]|\([^()]*\))*\{\{[^{}]*\}\}(?:[^(){}]|\([^()]*\))*\)/g, ' ')
    // "{{member_name}}'s notice" → "notice"
    .replace(/\{\{[^}]+\}\}\s*[’']s\b/g, ' ')
    // The placeholder and any quotes hugging it.
    .replace(/[“”‘’"']?\s*\{\{[^}]+\}\}\s*[“”‘’"']?/g, ' ')
    // Separators and dangling punctuation left behind by the removals.
    .replace(/\s*[·|]\s*/g, ' ')
    .replace(/\s*[#–—,:;]+\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[\s\-–—·,:;#'’]+|[\s\-–—·,:;#]+$/g, '')
    .trim();
  if (!cleaned) return action;
  // Summaries that opened with the member's name now start mid-sentence
  // ("paid their notice"); the list reads as headings, so capitalize.
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

export function countActiveActivityFilters(f: ActivityFilters): number {
  let n = 0;
  if (f.actorUserIds.length > 0) n++;
  if (f.actions.length > 0) n++;
  if (f.source) n++;
  if (f.createdAfter) n++;
  if (f.createdBefore) n++;
  return n;
}

export interface ActorOption {
  id: string;
  name: string;
}

// ── Panel ─────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: ActivityFilters;
  onChange: (f: ActivityFilters) => void;
  actorOptions: ActorOption[];
  actionOptions: string[];
}

export function ActivityFilterPanel({
  open,
  onOpenChange,
  filters,
  onChange,
  actorOptions,
  actionOptions,
}: Props) {
  const { t } = useTranslation('common');
  const activeCount = countActiveActivityFilters(filters);

  function toggleActor(id: string) {
    const next = filters.actorUserIds.includes(id)
      ? filters.actorUserIds.filter((x) => x !== id)
      : [...filters.actorUserIds, id];
    onChange({ ...filters, actorUserIds: next });
  }

  function toggleAction(action: string) {
    const next = filters.actions.includes(action)
      ? filters.actions.filter((a) => a !== action)
      : [...filters.actions, action];
    onChange({ ...filters, actions: next });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[22rem]"
      >
        {/* ── Header ── */}
        <div className="flex shrink-0 items-center gap-2 border-b border-border px-5 py-4">
          <Funnel className="h-4 w-4 text-muted-foreground" />
          <SheetTitle className="font-heading text-base font-bold">
            {t('settings.activity.filter.title')}
          </SheetTitle>
          {activeCount > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-pill bg-primary px-1.5 font-heading text-[0.65rem] font-bold text-primary-foreground">
              {activeCount}
            </span>
          )}
          <SheetDescription className="sr-only">
            {t('settings.activity.filter.description')}
          </SheetDescription>
        </div>

        {/* ── Scrollable sections ── */}
        <div className="flex-1 divide-y divide-border overflow-y-auto">

          {/* Actor */}
          {actorOptions.length > 0 && (
            <FilterSection title={t('settings.activity.filter.actor.title')}>
              <MultiSelectCombobox
                options={actorOptions.map((a) => ({ value: a.id, label: a.name }))}
                selected={filters.actorUserIds}
                onToggle={toggleActor}
                onClearAll={() => onChange({ ...filters, actorUserIds: [] })}
                allLabel={t('settings.activity.filter.actor.all')}
                selectedLabel={(n) =>
                  t('settings.activity.filter.actor.selected', { count: n })
                }
              />
            </FilterSection>
          )}

          {/* Action type */}
          {actionOptions.length > 0 && (
            <FilterSection title={t('settings.activity.filter.action.title')}>
              <button
                type="button"
                onClick={() =>
                  onChange({
                    ...filters,
                    actions: NOTICE_ACTION_GROUP.filter((a) => actionOptions.includes(a)),
                  })
                }
                className="mb-2 inline-flex items-center rounded-pill border border-border px-3 py-1 text-sm font-medium text-foreground/70 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t('settings.activity.filter.action.presetNotices')}
              </button>
              <MultiSelectCombobox
                options={actionOptions
                  .map((a) => ({ value: a, label: actionFilterLabel(t, a) }))
                  .sort((x, y) => x.label.localeCompare(y.label))}
                selected={filters.actions}
                onToggle={toggleAction}
                onClearAll={() => onChange({ ...filters, actions: [] })}
                allLabel={t('settings.activity.filter.action.all')}
                selectedLabel={(n) =>
                  t('settings.activity.filter.action.selected', { count: n })
                }
              />
            </FilterSection>
          )}

          {/* Source */}
          <FilterSection title={t('settings.activity.filter.source.title')}>
            <div className="flex flex-col gap-1.5">
              {(['', 'human', 'ai'] as const).map((val) => {
                const label = val === ''
                  ? t('settings.activity.filter.source.all')
                  : t(`settings.activity.filter.source.${val}`);
                return (
                  <button
                    key={val}
                    type="button"
                    onClick={() => onChange({ ...filters, source: val })}
                    className={cn(
                      'flex items-center rounded-lg border px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      filters.source === val
                        ? 'border-primary bg-primary/10 font-heading font-semibold text-foreground'
                        : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
                    )}
                  >
                    <span
                      className={cn(
                        'mr-2.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border',
                        filters.source === val ? 'border-primary bg-primary' : 'border-border',
                      )}
                    >
                      {filters.source === val && (
                        <span className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
                      )}
                    </span>
                    {label}
                  </button>
                );
              })}
            </div>
          </FilterSection>

          {/* Date range */}
          <FilterSection title={t('settings.activity.filter.dateRange.title')}>
            <div className="flex flex-col gap-3">
              <DateTimeField
                label={t('settings.activity.filter.dateRange.from')}
                value={filters.createdAfter}
                {...(filters.createdBefore ? { max: filters.createdBefore } : {})}
                onChange={(v) => onChange({ ...filters, createdAfter: v })}
              />
              <DateTimeField
                label={t('settings.activity.filter.dateRange.to')}
                value={filters.createdBefore}
                {...(filters.createdAfter ? { min: filters.createdAfter } : {})}
                onChange={(v) => onChange({ ...filters, createdBefore: v })}
              />
            </div>
          </FilterSection>
        </div>

        {/* ── Footer ── */}
        {activeCount > 0 && (
          <div className="shrink-0 border-t border-border px-5 py-4">
            <button
              type="button"
              onClick={() => onChange(EMPTY_ACTIVITY_FILTERS)}
              className="w-full rounded-pill border border-border py-2 font-heading text-sm font-semibold text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('settings.activity.filter.clearAll')}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3 px-5 py-4">
      <h3 className="font-heading text-[0.7rem] font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {children}
    </div>
  );
}

function MultiSelectCombobox({
  options,
  selected,
  onToggle,
  onClearAll,
  allLabel,
  selectedLabel,
}: {
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string) => void;
  onClearAll: () => void;
  allLabel: string;
  selectedLabel: (count: number) => string;
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);

  const triggerLabel =
    selected.length === 0 ? allLabel : selectedLabel(selected.length);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            selected.length > 0
              ? 'border-primary bg-primary/10 font-heading font-semibold text-foreground'
              : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <CaretDown
            className={cn('ml-2 h-3.5 w-3.5 shrink-0 transition-transform', open && 'rotate-180')}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[18rem] p-0" side="bottom" sideOffset={4}>
        <div className="max-h-56 overflow-y-auto py-1">
          {options.map((opt) => (
            <label
              key={opt.value}
              className="flex cursor-pointer items-center gap-2.5 px-3 py-1.5 hover:bg-muted/50"
            >
              <Checkbox
                checked={selected.includes(opt.value)}
                onChange={() => onToggle(opt.value)}
              />
              <span className="flex-1 truncate text-sm text-foreground">{opt.label}</span>
            </label>
          ))}
        </div>
        {selected.length > 0 && (
          <div className="border-t border-border px-3 py-2">
            <button
              type="button"
              onClick={onClearAll}
              className="font-heading text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none"
            >
              {t('settings.activity.filter.clearSection')}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function DateTimeField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: string;
  min?: string;
  max?: string;
  onChange: (v: string) => void;
}) {
  const { t, i18n } = useTranslation('common');
  const [open, setOpen] = useState(false);

  const datePart = value ? value.split('T')[0] : '';
  const timePart = value ? (value.split('T')[1] || '00:00') : '00:00';

  const displayValue = datePart
    ? new Intl.DateTimeFormat(i18n.language, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(new Date(`${datePart}T${timePart}`))
    : t('settings.activity.filter.dateRange.pick');

  function handleDateSelect(d: string) {
    onChange(`${d}T${timePart}`);
  }

  const [hourStr = '00', minStr = '00'] = timePart.split(':');
  const hourNum = Math.max(0, Math.min(23, parseInt(hourStr, 10) || 0));
  const minNum = Math.max(0, Math.min(59, parseInt(minStr, 10) || 0));

  function handleHourChange(e: React.ChangeEvent<HTMLInputElement>) {
    const h = Math.max(0, Math.min(23, parseInt(e.target.value || '0', 10)));
    const newTime = `${String(h).padStart(2, '0')}:${String(minNum).padStart(2, '0')}`;
    if (datePart) onChange(`${datePart}T${newTime}`);
  }

  function handleMinChange(e: React.ChangeEvent<HTMLInputElement>) {
    const m = Math.max(0, Math.min(59, parseInt(e.target.value || '0', 10)));
    const newTime = `${String(hourNum).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    if (datePart) onChange(`${datePart}T${newTime}`);
  }

  return (
    <div className="flex flex-col gap-1.5">
      <span className="font-heading text-xs font-semibold text-muted-foreground">{label}</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              datePart
                ? 'border-primary font-heading font-semibold text-foreground'
                : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
            )}
          >
            <CalendarBlank className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1 text-left">{displayValue}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-2" side="bottom" sideOffset={4}>
          <Calendar
            value={datePart || undefined}
            onSelect={handleDateSelect}
            min={min?.split('T')[0]}
            max={max?.split('T')[0]}
            locale={i18n.language}
          />
          <div className="border-t border-border px-1 pt-2">
            <div
              className={cn(
                'flex items-center justify-center gap-1 rounded-lg border border-border bg-background px-3 py-1.5 text-sm tabular-nums',
                !datePart && 'pointer-events-none opacity-40',
              )}
            >
              <input
                type="number"
                min={0}
                max={23}
                value={hourNum}
                disabled={!datePart}
                onChange={handleHourChange}
                className="w-8 bg-transparent text-center focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                aria-label="hours"
              />
              <span className="select-none text-muted-foreground">:</span>
              <input
                type="number"
                min={0}
                max={59}
                value={minNum}
                disabled={!datePart}
                onChange={handleMinChange}
                className="w-8 bg-transparent text-center focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                aria-label="minutes"
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="self-start font-heading text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none"
        >
          <X className="mr-0.5 inline h-3 w-3" />
          {t('settings.activity.filter.clearSection')}
        </button>
      )}
    </div>
  );
}
